import { useEffect, useState } from "react";
import { setDueSoonDays } from "./format";
import type { Mode, ThemeName, Density } from "./types";
import { DEFAULT_ICON_MARK, isIconMark, type IconMark } from "./appIcons";

const THEME_KEY = "ph.theme";
const MODE_KEY = "ph.mode";
const DENSITY_KEY = "ph.density";
const COMPLETED_KEY = "ph.showCompleted";

let mql: MediaQueryList | null = null;
let autoListener: (() => void) | null = null;

function resolveMode(mode: Mode): "light" | "dark" {
  if (mode === "auto") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return mode;
}

/**
 * Write theme + mode onto <html> synchronously. Called on click so the change
 * lands in the same frame rather than a render cycle later.
 */
export function applyTheme(theme: ThemeName, mode: Mode): void {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  root.setAttribute("data-mode", resolveMode(mode));
  applyAppIcon(theme);

  // Keep `auto` live: re-apply when the OS flips.
  if (autoListener && mql) {
    mql.removeEventListener("change", autoListener);
    autoListener = null;
  }
  if (mode === "auto") {
    mql = window.matchMedia("(prefers-color-scheme: dark)");
    autoListener = () => {
      root.setAttribute("data-mode", resolveMode("auto"));
    };
    mql.addEventListener("change", autoListener);
  }
}

// ---- App icon ---------------------------------------------------------------
// iOS reads the apple-touch-icon, and an installed app its manifest, at the
// moment you add it to the home screen or Dock. Safari takes those from the page
// as served, not as rewritten here, so the choice also goes in a `ph_icon`
// cookie and the Worker answers the standard icon URLs with that theme's set
// (themedIcon in worker/index.ts; the sets come from scripts/make-icons.mjs).
// The <link>s are swapped as well, which keeps the browser tab's icon in step.
// It is a snapshot: changing the colour later does not repaint an icon already
// installed — remove it and add it again.

const ICON_KEY = "ph.iconFollowsTheme";
const iconListeners = new Set<(on: boolean) => void>();

export function loadIconFollowsTheme(): boolean {
  try { return localStorage.getItem(ICON_KEY) !== "off"; } catch { return true; }
}

export function saveIconFollowsTheme(on: boolean): void {
  try { localStorage.setItem(ICON_KEY, on ? "on" : "off"); } catch { /* private mode */ }
  applyAppIcon(document.documentElement.getAttribute("data-theme") as ThemeName || DEFAULT_THEME);
  for (const fn of iconListeners) fn(on);
}

export function useIconFollowsTheme(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(loadIconFollowsTheme);
  useEffect(() => {
    iconListeners.add(setOn);
    return () => { iconListeners.delete(setOn); };
  }, []);
  return [on, saveIconFollowsTheme];
}

// Which design (lib/appIcons.ts). Same snapshot rule as the colour, and the
// same route to the Worker: a `ph_mark` cookie, absent for the folder.
const MARK_KEY = "ph.iconMark";
const markListeners = new Set<(m: IconMark) => void>();

export function loadIconMark(): IconMark {
  try {
    const v = localStorage.getItem(MARK_KEY);
    return isIconMark(v) ? v : DEFAULT_ICON_MARK;
  } catch { return DEFAULT_ICON_MARK; }
}

export function saveIconMark(mark: IconMark): void {
  try { localStorage.setItem(MARK_KEY, mark); } catch { /* private mode */ }
  applyAppIcon(document.documentElement.getAttribute("data-theme") as ThemeName || DEFAULT_THEME);
  for (const fn of markListeners) fn(mark);
}

export function useIconMark(): [IconMark, (m: IconMark) => void] {
  const [mark, setMark] = useState(loadIconMark);
  useEffect(() => {
    markListeners.add(setMark);
    return () => { markListeners.delete(setMark); };
  }, []);
  return [mark, saveIconMark];
}

function applyAppIcon(theme: ThemeName): void {
  const follow = loadIconFollowsTheme() && KNOWN.has(theme);
  const mark = loadIconMark();
  document.cookie = follow
    ? `ph_icon=${theme}; path=/; max-age=31536000; SameSite=Lax`
    : "ph_icon=; path=/; max-age=0; SameSite=Lax";
  document.cookie = mark !== DEFAULT_ICON_MARK
    ? `ph_mark=${mark}; path=/; max-age=31536000; SameSite=Lax`
    : "ph_mark=; path=/; max-age=0; SameSite=Lax";
  const base = mark !== DEFAULT_ICON_MARK
    ? `/icons/${mark}/${follow ? theme : DEFAULT_THEME}/`
    : follow ? `/icons/${theme}/` : "/";
  const set = (selector: string, file: string) => {
    const el = document.head.querySelector<HTMLLinkElement>(selector);
    if (el) el.href = base + file;
  };
  set('link[rel="apple-touch-icon"]', "apple-touch-icon.png");
  set('link[rel="icon"][type="image/svg+xml"]', "favicon.svg");
  set('link[rel="manifest"]', "site.webmanifest");
}

/** Blue is the default — it is what the app icon and the login screen use. */
export const DEFAULT_THEME: ThemeName = "blue";

/**
 * Themes used to be named for materials; they are named for hues now. A stored
 * preference outlives the rename, and an unknown data-theme silently falls back
 * to the :root defaults — so the picker would show blue while localStorage
 * still said "clay". Map the old names to their nearest hue instead.
 */
const RENAMED: Record<string, ThemeName> = {
  jade: "green", sage: "green", slate: "blue", clay: "orange",
  // Purple and pink were dropped so the picker fits on one line.
  purple: "indigo", pink: "red",
};
const KNOWN = new Set<string>([
  "green", "teal", "blue", "indigo", "red", "orange", "graphite",
]);

function normalizeTheme(stored: string | null): ThemeName {
  if (!stored) return DEFAULT_THEME;
  if (KNOWN.has(stored)) return stored as ThemeName;
  return RENAMED[stored] ?? DEFAULT_THEME;
}
export const DEFAULT_MODE: Mode = "light";

export function loadTheme(): { theme: ThemeName; mode: Mode } {
  const theme = normalizeTheme(localStorage.getItem(THEME_KEY));
  const mode = (localStorage.getItem(MODE_KEY) as Mode) || DEFAULT_MODE;
  return { theme, mode };
}

export function saveTheme(theme: ThemeName, mode: Mode): void {
  localStorage.setItem(THEME_KEY, theme);
  localStorage.setItem(MODE_KEY, mode);
}

/** Call once at boot, before first paint, to avoid a flash. */
export function initTheme(): { theme: ThemeName; mode: Mode } {
  const { theme, mode } = loadTheme();
  applyTheme(theme, mode);
  return { theme, mode };
}

/**
 * Adopt the appearance stored on the signed-in account.
 *
 * localStorage alone is per-browser AND perishable: Safari evicts it after
 * about seven days without a visit, which on a phone is easy to hit — the
 * setting looks like it "didn't save" when in fact the whole key is gone. The
 * `users` record is the durable copy and follows you between devices.
 *
 * Local wins during the session (it is what you just clicked); the record is
 * only read at sign-in, and only when it actually holds something.
 */
export function adoptAccountTheme(record: { theme?: string; mode?: string } | null): void {
  if (!record) return;
  // Through normalizeTheme, not a bare cast: an account saved before the
  // material names became hue names still has "sage" or "clay" on the server.
  const theme = record.theme ? normalizeTheme(record.theme) : undefined;
  const mode = record.mode as Mode | undefined;
  if (!theme && !mode) return;

  const current = loadTheme();
  const next = { theme: theme || current.theme, mode: mode || current.mode };
  saveTheme(next.theme, next.mode);
  applyTheme(next.theme, next.mode);
}

// ---- Completed items -------------------------------------------------------
// One preference, two surfaces: the Grid toolbar pill (§4) and
// Settings › Appearance. Both read and write through here, and listeners keep
// any mounted surface in step rather than waiting for a remount.

type CompletedListener = (shown: boolean) => void;
const completedListeners = new Set<CompletedListener>();

export function loadShowCompleted(): boolean {
  return localStorage.getItem(COMPLETED_KEY) !== "hidden";
}

export function saveShowCompleted(shown: boolean): void {
  localStorage.setItem(COMPLETED_KEY, shown ? "shown" : "hidden");
  for (const fn of completedListeners) fn(shown);
}

/** Read + write the shared preference, staying in sync with the other surface. */
export function useShowCompleted(): [boolean, (shown: boolean) => void] {
  const [shown, setShown] = useState(loadShowCompleted);
  useEffect(() => {
    completedListeners.add(setShown);
    return () => { completedListeners.delete(setShown); };
  }, []);
  return [shown, saveShowCompleted];
}

// ---- Pinned projects --------------------------------------------------------

const PINS_KEY = "ph.pinnedProjects";
const pinListeners = new Set<(v: string[]) => void>();

export function loadPins(): string[] {
  try {
    const raw = localStorage.getItem(PINS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch { return []; }
}

function savePins(ids: string[]): void {
  try { localStorage.setItem(PINS_KEY, JSON.stringify(ids)); } catch { /* private mode */ }
  for (const fn of pinListeners) fn(ids);
}

export function usePinnedProjects(): [string[], (id: string) => void] {
  const [pins, setPins] = useState<string[]>(loadPins);
  useEffect(() => {
    pinListeners.add(setPins);
    return () => { pinListeners.delete(setPins); };
  }, []);
  const toggle = (id: string) => {
    const next = pins.includes(id) ? pins.filter((p) => p !== id) : [...pins, id];
    savePins(next);
  };
  return [pins, toggle];
}

// ---- Activities -------------------------------------------------------------

/**
 * A short library of things you do, to fill "What are you working on?".
 *
 * Not categories in the taxonomy sense — this is a typing shortcut that happens
 * to be worth counting. Picking "Email" writes it into the description AND
 * records it on the entry, so "how much of last month went on email" becomes a
 * real breakdown rather than a guess at what you typed that week.
 *
 * Stored in the entry's `category` field, which already existed for exactly
 * this shape of data and was previously only ever set on ad-hoc time.
 */
const ACTIVITIES_KEY = "ph.activities";
// Sorted here as well as on save: the defaults are what most people will only
// ever see, and an unsorted default next to a sorted edited list reads as a bug.
const DEFAULT_ACTIVITIES = ["Admin", "Client call", "Email", "Internal meeting", "Proposal", "Research", "Travel", "Writing"];
/**
 * Activities added to the defaults after people already had a saved list. Each
 * is offered once, then left to you: delete it and it stays deleted.
 */
const ADDED_LATER: { name: string; flag: string }[] = [
  // Pairs with Internal time (lib/billing): proposal hours, kept countable.
  { name: "Proposal", flag: "ph.activities.added.proposal" },
];
const activityListeners = new Set<(v: string[]) => void>();

export function loadActivities(): string[] {
  try {
    const raw = localStorage.getItem(ACTIVITIES_KEY);
    if (!raw) return DEFAULT_ACTIVITIES;
    let list = JSON.parse(raw) as string[];
    for (const { name, flag } of ADDED_LATER) {
      if (localStorage.getItem(flag)) continue;
      localStorage.setItem(flag, "1");
      if (!list.some((a) => a.toLowerCase() === name.toLowerCase())) {
        list = [...list, name].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
        localStorage.setItem(ACTIVITIES_KEY, JSON.stringify(list));
      }
    }
    return list;
  } catch { return DEFAULT_ACTIVITIES; }
}

function saveActivities(list: string[]): void {
  // Deduped case-insensitively and sorted, so the picker reads the same way
  // wherever it appears and you cannot end up with "Email" and "email".
  const seen = new Set<string>();
  const next = list
    .map((a) => a.trim())
    .filter((a) => {
      const key = a.toLowerCase();
      if (!a || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  try { localStorage.setItem(ACTIVITIES_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  for (const fn of activityListeners) fn(next);
}

export function useActivities(): [string[], (list: string[]) => void] {
  const [list, setList] = useState<string[]>(loadActivities);
  useEffect(() => {
    activityListeners.add(setList);
    return () => { activityListeners.delete(setList); };
  }, []);
  return [list, saveActivities];
}

// ---- Planning defaults (§11) -----------------------------------------------
// One small factory rather than three copies of the same listener plumbing.

function makePref<T extends string>(key: string, fallback: T, apply?: (v: T) => void) {
  const listeners = new Set<(v: T) => void>();
  const load = (): T => (localStorage.getItem(key) as T) || fallback;
  const save = (v: T): void => {
    localStorage.setItem(key, v);
    apply?.(v);
    for (const fn of listeners) fn(v);
  };
  const use = (): [T, (v: T) => void] => {
    const [value, setValue] = useState<T>(load);
    useEffect(() => {
      listeners.add(setValue);
      return () => { listeners.delete(setValue); };
    }, []);
    return [value, save];
  };
  return { load, save, use, apply };
}

export type DefaultTab = "Board" | "Grid" | "Timeline" | "Calendar" | "Notes" | "Overview";
export type BoardGrouping = "phase" | "status";
export type DueSoon = "1" | "3" | "7";

const defaultTabPref = makePref<DefaultTab>("ph.defaultTab", "Board");
export const loadDefaultTab = defaultTabPref.load;
export const useDefaultTab = defaultTabPref.use;

/**
 * Billing increment for stored durations. "off" keeps them exact.
 *
 * Off by default: rounding is a billing decision, and turning it on silently
 * would quietly change what every future entry says.
 */
export type Rounding = "off" | "5" | "6" | "10" | "15";
const roundingPref = makePref<Rounding>("ph.rounding", "off");
export const loadRounding = roundingPref.load;
export const useRounding = roundingPref.use;

/** Minutes to snap to, or 1 — which `applyRounding` treats as no rounding. */
export function roundingMinutes(): number {
  const v = loadRounding();
  return v === "off" ? 1 : Number(v);
}

/**
 * How long a timer runs before you are asked whether it is still meant to.
 *
 * A timer left running over lunch, or overnight, is the most expensive mistake
 * a timesheet has: it either bills a client for hours nobody worked or gets
 * deleted and loses the real hours inside it. The nudge repeats at every
 * multiple (3h, 6h, 9h…) so an overnight timer is caught at breakfast, not
 * only at the first threshold nobody was awake for.
 */
export type TimerNudge = "off" | "1" | "2" | "3" | "4" | "6";
const timerNudgePref = makePref<TimerNudge>("ph.timerNudge", "3");
export const loadTimerNudge = timerNudgePref.load;
export const useTimerNudge = timerNudgePref.use;

/** The Weekly review page (views/WeeklyReview.tsx) — on trial, so switchable. */
const weeklyReviewPref = makePref<"on" | "off">("ph.weeklyReview", "on");
export const useWeeklyReview = weeklyReviewPref.use;

const boardGroupingPref = makePref<BoardGrouping>("ph.boardGrouping", "phase");
export const loadBoardGrouping = boardGroupingPref.load;
export const useBoardGrouping = boardGroupingPref.use;

// §15.4 — two list preferences that only the phone surfaces.
export type OnOff = "on" | "off";
const swipeDeferPref = makePref<OnOff>("ph.swipeDefer", "on");
export const loadSwipeDefer = swipeDeferPref.load;
export const useSwipeDefer = swipeDeferPref.use;

const badgeTabsPref = makePref<OnOff>("ph.badgeTabs", "on");
export const loadBadgeTabs = badgeTabsPref.load;
export const useBadgeTabs = badgeTabsPref.use;

const dueSoonPref = makePref<DueSoon>("ph.dueSoon", "3", (v) => setDueSoonDays(Number(v)));
export const useDueSoon = dueSoonPref.use;

/**
 * Work vs personal. Todoist has no workspaces below the Business plan, so this
 * split is Project Hub's own — see `Project.category`. Persisted because the
 * answer is almost always the same one, and re-picking it every visit is noise.
 */
export type CategoryFilter = "all" | "work" | "personal";
const categoryPref = makePref<CategoryFilter>("ph.category", "all");
export const loadCategoryFilter = categoryPref.load;
export const useCategoryFilter = categoryPref.use;

/** Call once at boot so the threshold is live before the first render. */
export function initPlanningDefaults(): void {
  setDueSoonDays(Number(dueSoonPref.load()));
}

// ---- Density ---------------------------------------------------------------

export function applyDensity(density: Density): void {
  document.documentElement.setAttribute("data-density", density);
}
export function loadDensity(): Density {
  return (localStorage.getItem(DENSITY_KEY) as Density) || "comfortable";
}
export function saveDensity(density: Density): void {
  localStorage.setItem(DENSITY_KEY, density);
}
/** Call once at boot alongside initTheme(). */
export function initDensity(): Density {
  const density = loadDensity();
  applyDensity(density);
  return density;
}

/** Logged time on Today: the lane on the grid, the row above it and the total. */
const todayLoggedPref = makePref<OnOff>("ph.todayLogged", "on");
export const useTodayLogged = todayLoggedPref.use;

/**
 * The order of the top bar's tabs, as their paths joined with commas. Empty
 * means the built-in order. A tab the saved order doesn't name (one added
 * since) keeps its built-in place after the ones it does, so a new page never
 * goes missing because an older order was saved.
 */
const navOrderPref = makePref<string>("ph.navOrder", "");
export const useNavOrder = navOrderPref.use;

export function inNavOrder<T extends { to: string }>(tabs: T[], order: string): T[] {
  const keys = order.split(",").filter(Boolean);
  const rank = (t: T) => {
    const i = keys.indexOf(t.to);
    return i === -1 ? keys.length + tabs.indexOf(t) : i;
  };
  return [...tabs].sort((a, b) => rank(a) - rank(b));
}
